/**
 * Calibre Tier Names — one owner's `tierVocabulary` list (design memo P1b).
 *
 * Shared by the movement-manufacturer editor and the calibre-family editor,
 * which differ only in where the list is stored and what happens around an
 * add (the family's promote prompt). Owner-specific behaviour arrives as
 * `TierVocabularyOwner`, the same arrangement as `TimelineSection`.
 *
 * PERSISTENCE (D4): every change is its own transactional write through
 * `owner.mutate` — add, edit, delete, and the AI dialog's Merge & Save. There
 * is no section Save button and the list is not part of the form's Save, so a
 * write from outside the form (the promote prompt) can't be overwritten by a
 * held-back copy.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import toast from 'react-hot-toast';
import type { TierVocabularyEntry } from '@watchkeeper/shared-types';
import { Button } from '../common/Button';
import {
  getWatchBrandsByIds,
  onTierVocabularyChanged,
  type SuggestTierVocabResult,
} from '../../services/firebase';
import {
  mergeSuggestedTiers,
  removeTierEntry,
  sortTierVocabulary,
  upsertTierEntry,
  type AcceptedTierSuggestion,
} from '../../utils/tierVocabulary';
import { TierVocabEntryModal } from './TierVocabEntryModal';
import { SuggestTierNamesDialog } from './SuggestTierNamesDialog';

export interface TierVocabularyOwner {
  kind: 'manufacturer' | 'family';
  id: string;
  /** "Sellita" / "SW200 family" — dialog titles and toasts. */
  name: string;
  entries: TierVocabularyEntry[];
  /** One transactional change; resolves with the list as written. */
  mutate: (change: (current: TierVocabularyEntry[]) => TierVocabularyEntry[]) => Promise<TierVocabularyEntry[]>;
  /** Another list this one must not shadow (the family passes the manufacturer's). */
  reserved?: TierVocabularyEntry[];
  /** Present ⇒ the ✨ Suggest tier names button is offered. */
  suggest?: (hint?: string) => Promise<SuggestTierVocabResult>;
  /**
   * Called with a NEW entry before it is written (the family's promote prompt).
   * 'write' = the section writes it as usual; 'handled' = the host wrote it
   * elsewhere, so just close; 'cancel' = nothing was written and the entry
   * form stays open with what the curator typed.
   */
  interceptAdd?: (entry: TierVocabularyEntry) => Promise<'write' | 'handled' | 'cancel'>;
}

interface Props {
  owner: TierVocabularyOwner;
  /** Short explainer under the header. */
  intro?: ReactNode;
  /** Extra control per row — the family's quiet "Promote to …" link. */
  rowExtra?: (entry: TierVocabularyEntry) => ReactNode;
}

type EditorTarget = null | 'new' | TierVocabularyEntry;

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function TierVocabularySection({ owner, intro, rowExtra }: Props) {
  const [entries, setEntries] = useState<TierVocabularyEntry[]>(owner.entries);
  const [editing, setEditing] = useState<EditorTarget>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [brandNames, setBrandNames] = useState<Record<string, string>>({});

  // A fresh copy from the host (re-opened editor) replaces ours. Keyed on the
  // content, not the array: a host passing `x ?? []` hands over a new array
  // every render, and re-seeding on identity would loop.
  const seedKey = JSON.stringify(owner.entries);
  useEffect(() => setEntries(owner.entries), [seedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Writes from elsewhere on the page (the family's promote prompt writes the
  // manufacturer list) land here too.
  useEffect(
    () =>
      onTierVocabularyChanged((kind, id, next) => {
        if (kind === owner.kind && id === owner.id) setEntries(next);
      }),
    [owner.kind, owner.id],
  );

  const brandIdsKey = useMemo(
    () => [...new Set(entries.map((e) => e.brandId).filter((b): b is string => !!b))].sort().join(','),
    [entries],
  );
  useEffect(() => {
    const ids = brandIdsKey ? brandIdsKey.split(',') : [];
    const missing = ids.filter((id) => !(id in brandNames));
    if (missing.length === 0) return;
    let cancelled = false;
    getWatchBrandsByIds(missing)
      .then((brands) => {
        if (cancelled) return;
        setBrandNames((prev) => {
          const next = { ...prev };
          for (const id of missing) next[id] = brands.find((b) => b.id === id)?.name ?? id;
          return next;
        });
      })
      .catch((err) => console.warn('Tier names: brand name lookup failed', err));
    return () => {
      cancelled = true;
    };
    // brandNames is deliberately not a dependency: it only grows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandIdsKey]);

  const sorted = useMemo(() => sortTierVocabulary(entries), [entries]);
  const genericCount = entries.filter((e) => !e.brandId).length;

  const write = useCallback(
    async (change: (current: TierVocabularyEntry[]) => TierVocabularyEntry[], success: string) => {
      try {
        const next = await owner.mutate(change);
        setEntries(next);
        toast.success(success);
      } catch (err) {
        console.error('Tier names write failed:', err);
        toast.error(`Could not save: ${errorText(err)}`);
        throw err;
      }
    },
    [owner],
  );

  const handleSave = useCallback(
    async (entry: TierVocabularyEntry) => {
      const isNew = editing === 'new';
      if (isNew && owner.interceptAdd) {
        let outcome: 'write' | 'handled' | 'cancel';
        try {
          outcome = await owner.interceptAdd(entry);
        } catch (err) {
          toast.error(`Could not save: ${errorText(err)}`);
          throw err;
        }
        if (outcome === 'cancel') return;
        if (outcome === 'handled') {
          setEditing(null);
          return;
        }
      }
      await write((cur) => upsertTierEntry(cur, entry), isNew ? `Added “${entry.canonicalName}”.` : `Saved “${entry.canonicalName}”.`);
      setEditing(null);
    },
    [editing, owner, write],
  );

  const handleDelete = useCallback(
    async (entry: TierVocabularyEntry) => {
      if (!window.confirm(`Remove “${entry.canonicalName}” from ${owner.name}’s tier names? Tiers already stored on calibres keep their names.`)) {
        return;
      }
      setBusy(true);
      try {
        await write((cur) => removeTierEntry(cur, entry.id), `Removed “${entry.canonicalName}”.`);
      } catch {
        // toasted
      } finally {
        setBusy(false);
      }
    },
    [owner.name, write],
  );

  const handleApplySuggestions = useCallback(
    async (accepted: AcceptedTierSuggestion[]) => {
      await write((cur) => mergeSuggestedTiers(cur, accepted), `Merged ${accepted.length} suggestion${accepted.length === 1 ? '' : 's'} into ${owner.name}’s tier names.`);
      setSuggestOpen(false);
    },
    [owner.name, write],
  );

  return (
    <div className="border-t pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Calibre Tier Names</h3>
        <span className="text-[11px] text-gray-500">
          {genericCount} grade{genericCount === 1 ? '' : 's'}
          {entries.length > genericCount ? ` · ${entries.length - genericCount} brand tier${entries.length - genericCount === 1 ? '' : 's'}` : ''}
          {' · saves immediately'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')} disabled={busy}>
            + Add tier name
          </Button>
          {owner.suggest && (
            <Button variant="secondary" size="sm" onClick={() => setSuggestOpen(true)} disabled={busy} title="Propose grade names with AI, review them, and save in one step.">
              ✨ Suggest tier names
            </Button>
          )}
        </div>
      </div>
      {intro && <div className="mt-1 text-[11px] text-gray-500">{intro}</div>}

      {sorted.length === 0 ? (
        <p className="mt-2 text-xs text-gray-400 italic">No tier names yet.</p>
      ) : (
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b">
              <th className="py-1 pr-2 w-10">Rank</th>
              <th className="py-1 pr-2">Name</th>
              <th className="py-1 pr-2">Other spellings</th>
              <th className="py-1 pr-2">Brand</th>
              <th className="py-1 pr-2">Description</th>
              <th className="py-1 w-28" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((e) => (
              <tr key={e.id} className="border-b last:border-0 align-top">
                <td className="py-1.5 pr-2 text-gray-500">{e.rank ?? ''}</td>
                <td className="py-1.5 pr-2 font-medium text-gray-900">{e.canonicalName}</td>
                <td className="py-1.5 pr-2">
                  <div className="flex flex-wrap gap-1">
                    {e.akas.map((a) => (
                      <span key={a} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                        {a}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-1.5 pr-2 text-xs">
                  {e.brandId ? (
                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200">{brandNames[e.brandId] ?? e.brandId}</span>
                  ) : (
                    <span className="text-gray-400">generic</span>
                  )}
                </td>
                <td className="py-1.5 pr-2 text-xs text-gray-600">{e.description}</td>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {rowExtra?.(e)}
                  <button type="button" className="text-xs text-brand-600 hover:underline mr-2" onClick={() => setEditing(e)} disabled={busy}>
                    Edit
                  </button>
                  <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => void handleDelete(e)} disabled={busy} title={`Remove “${e.canonicalName}”`}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <TierVocabEntryModal
          initial={editing === 'new' ? null : editing}
          list={entries}
          reserved={owner.reserved}
          brandNames={brandNames}
          ownerName={owner.name}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
      {suggestOpen && owner.suggest && (
        <SuggestTierNamesDialog
          ownerName={owner.name}
          entries={entries}
          suggest={owner.suggest}
          onClose={() => setSuggestOpen(false)}
          onApply={handleApplySuggestions}
        />
      )}
    </div>
  );
}
